import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';

import type { AppDispatch } from '../store';
import type { ChecklistItemStatusEnum } from '@shared/enums/checklistItemStatus.enum';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { ChecklistFormPayloadModel } from '@shared/models/checklists/checklistFormPayload.model';
import type { ChecklistItemModel } from '@shared/models/checklists/checklistItem.model';
import type { GetChecklistsResponseModel } from '@shared/models/checklists/getChecklistsResponse.model';
import type { TaskItemModel } from '@shared/models/tasks/taskItem.model';

export const getChecklists = createAsyncThunk<
  GetChecklistsResponseModel,
  { entity_type?: string; entity_id?: number },
  { rejectValue: ErrorType }
>('getChecklists', async (params, { rejectWithValue }) => {
  try {
    return await api.checklists.getChecklists(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const createChecklistItem = createAsyncThunk<
  ChecklistItemModel,
  ChecklistFormPayloadModel & { entity_type?: string; entity_id?: number },
  { rejectValue: ErrorType }
>('createChecklistItem', async (params, { rejectWithValue }) => {
  try {
    return await api.checklists.addChecklistItem(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateChecklistItem = createAsyncThunk<
  ChecklistItemModel,
  { status: ChecklistItemStatusEnum; id: number; entity_type?: string; entity_id?: number },
  { rejectValue: ErrorType }
>('updateChecklistItem', async (params, { rejectWithValue }) => {
  try {
    return await api.checklists.updateChecklistItem(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const handleSortChecklists = createAsyncThunk<
  any,
  { id: number; index: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('handleSortChecklists', async ({ id, index }, { rejectWithValue }) => {
  try {
    return await api.checklists.sortChecklists(id, { position: index });
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateChecklistItemBody = createAsyncThunk<
  ChecklistItemModel,
  { body: string; id: number; entity_type?: string; entity_id?: number },
  { rejectValue: ErrorType }
>('updateChecklistItemBody', async (params, { rejectWithValue }) => {
  try {
    return await api.checklists.updateChecklistItemBody(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getChecklistItem = createAsyncThunk<ChecklistItemModel, number, { rejectValue: ErrorType }>(
  'deleteChecklistItem',
  async (props, { rejectWithValue }) => {
    try {
      return await api.checklists.getChecklistItem(props);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const removeChecklistItem = createAsyncThunk<
  number,
  { itemId: number; entity_type?: string; entity_id?: number },
  { rejectValue: ErrorType }
>('deleteChecklistItem', async (props, { rejectWithValue }) => {
  try {
    return await api.checklists.deleteChecklistItem(props).then(() => props.itemId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
export const convertChecklistItemToTask = createAsyncThunk<
  TaskItemModel,
  { checklistId: number; plannerItemId: number; title: string },
  { rejectValue: ErrorType }
>('convertChecklistItemToTask', async (props, { rejectWithValue }) => {
  try {
    return await api.checklists.convertChecklistItemToTask(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
